/** Clients resident in these countries (ISO 3166-1 alpha-2), or sending from them, cannot deposit. */
export const RESTRICTED_COUNTRIES = ['DE', 'US', 'IL', 'CY'];
export const isRestrictedCountry = (cc) => !!cc && RESTRICTED_COUNTRIES.includes(cc.toUpperCase());
/** RESTRICTED_COUNTRIES in prose, for client-facing copy. */
export const RESTRICTED_COUNTRY_NAMES = 'Germany, the United States, Israel or Cyprus';
export const DEPOSIT_CURRENCIES = ['USD', 'GBP', 'EUR'];
export const CURRENCY_SYMBOL = { USD: '$', GBP: '£', EUR: '€' };
const GBP_COUNTRIES = ['GB', 'IM', 'JE', 'GG'];
const EUR_COUNTRIES = ('AD AT BE BG CH CY CZ DE DK EE ES FI FR GR HR HU IE IS IT LI LT LU LV MC ME MT NL NO PL PT RO SE SI SK SM VA AL BA MK RS').split(' ');
/** The currency a client from `country` most likely sends: GBP in the UK, EUR in Europe, else USD. */
export function defaultDepositCurrency(country) {
    const cc = (country ?? '').toUpperCase();
    if (GBP_COUNTRIES.includes(cc))
        return 'GBP';
    if (EUR_COUNTRIES.includes(cc))
        return 'EUR';
    return 'USD';
}
/** Methods a client can request on the Deposit page (agent / other remain for older requests). */
export const REQUESTABLE_DEPOSIT_METHODS = ['bank_wire', 'card'];
/** Minimum deposit in USD (or its estimated USD value) per method. */
export const DEPOSIT_MIN_USD = { bank_wire: '250', card: '100' };
export const CONTACT_METHODS = [
    { id: 'phone', label: 'Phone call' },
    { id: 'whatsapp', label: 'WhatsApp' },
    { id: 'email', label: 'Email' },
];
export const contactLabel = (m) => CONTACT_METHODS.find((c) => c.id === m)?.label ?? m ?? '';
/** "will call you", "will message you on WhatsApp", "will email you". */
export const contactPhrase = (m) => (m === 'whatsapp' ? 'message you on WhatsApp' : m === 'email' ? 'email you' : 'call you');
// ---- call-back booking ----
/**
 * When the account managers take calls: Monday to Friday 08:00–20:00 London time, in 30-minute
 * slots, bookable up to 30 days ahead. The default booking is the soonest time, 15 minutes after the
 * request; outside hours it is the next opening. Clients see every time in their own time zone.
 */
export const CALL_HOURS = { tz: 'Europe/London', days: [1, 2, 3, 4, 5], open: 8 * 60, close: 20 * 60, stepMin: 30, leadMin: 15, maxDays: 30 };
const MIN = 60_000;
const STEP = CALL_HOURS.stepMin * MIN;
const WD = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const fmt = new Map();
function local(t, tz) {
    let f = fmt.get(tz);
    if (!f) {
        f = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
        fmt.set(tz, f);
    }
    let day = 0;
    let minutes = 0;
    for (const p of f.formatToParts(new Date(t))) {
        if (p.type === 'weekday')
            day = WD[p.value] ?? 0;
        else if (p.type === 'hour')
            minutes += Number(p.value) * 60;
        else if (p.type === 'minute')
            minutes += Number(p.value);
    }
    return { day, minutes };
}
/** True when `t` (epoch ms) is inside call hours. */
export function isCallOpen(t) {
    const { day, minutes } = local(t, CALL_HOURS.tz);
    return CALL_HOURS.days.includes(day) && minutes >= CALL_HOURS.open && minutes < CALL_HOURS.close;
}
/** The default call time: 15 minutes from `now` (rounded up to the minute), or the next opening after it. */
export function soonestCall(now = Date.now()) {
    const t = Math.ceil((now + CALL_HOURS.leadMin * MIN) / MIN) * MIN;
    if (isCallOpen(t))
        return t;
    for (let s = Math.ceil(t / STEP) * STEP, end = t + 10 * 86_400_000; s < end; s += STEP)
        if (isCallOpen(s))
            return s;
    return t;
}
/** Bookable 30-minute slot times in [from, to). */
export function callSlots(from, to) {
    const out = [];
    for (let s = Math.ceil(from / STEP) * STEP; s < to; s += STEP)
        if (isCallOpen(s))
            out.push(s);
    return out;
}
/** Latest time a call can be booked for. */
export const lastCallTime = (now = Date.now()) => now + CALL_HOURS.maxDays * 86_400_000;
/**
 * Check a requested call time. A time that has (nearly) passed since the page was opened becomes the
 * soonest time; anything else must be inside call hours and within 30 days. null = not bookable.
 */
export function normalizeCallTime(t, now = Date.now()) {
    if (!Number.isFinite(t) || t < now - 60 * MIN)
        return null;
    if (t < now + 10 * MIN)
        return soonestCall(now);
    if (t > lastCallTime(now) + 86_400_000)
        return null;
    return isCallOpen(t) ? t : null;
}
/** True for a valid IANA time zone name. */
export function isTimeZone(tz) {
    if (typeof tz !== 'string' || !tz || tz.length > 64)
        return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}
/** "Fri, 9 Oct · 2:30 pm" in `tz` (the client's time zone). */
export function formatCallTime(t, tz, h24 = false) {
    const d = new Intl.DateTimeFormat('en-GB', { timeZone: tz, weekday: 'short', day: 'numeric', month: 'short' }).format(t);
    const h = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hourCycle: h24 ? 'h23' : 'h12' }).format(t);
    return `${d} · ${h24 ? h : h.replace(' AM', ' am').replace(' PM', ' pm')}`;
}
//# sourceMappingURL=deposits.js.map